import { useId, type ReactNode, type SelectHTMLAttributes } from 'react'
import { cn } from '../cn.js'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  options: SelectOption[]
  /** Rendered as a disabled first option, so nothing is silently pre-chosen. */
  placeholder?: string
  hint?: ReactNode
  error?: string | undefined
}

/**
 * A native `<select>`, deliberately.
 *
 * A custom listbox would be prettier and would also be the component most
 * likely to break: this funnel's traffic is majority mobile and includes a
 * large share of in-app browsers (§11.1), where the platform picker is the one
 * control guaranteed to work, to be reachable by assistive tech, and to handle
 * Thai and CJK labels without a font surprise.
 */
export function Select({
  label,
  options,
  placeholder,
  hint,
  error,
  className,
  required,
  value,
  ...rest
}: SelectProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <select
        id={id}
        required={required}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={cn(
          'min-h-[44px] rounded-lg border bg-white px-3 py-2 text-ink',
          error ? 'border-danger' : 'border-line-strong',
          'disabled:bg-surface-alt disabled:text-muted',
          // An unchosen select must not look like a filled one, or people skip
          // past it and meet the error only on submit.
          value === '' && 'text-muted-soft',
          className,
        )}
        {...rest}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} className="text-ink">
            {option.label}
          </option>
        ))}
      </select>

      {error ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
